import { useNotifyStore } from '@/stores/notifications'
import { uuid } from '@/composables/uuid'

/**
 * Copy text to clipboard with robust fallbacks and notifications.
 * - Uses navigator.clipboard when available and permitted (HTTPS only).
 * - Falls back to document.execCommand('copy') via a hidden textarea when needed.
 * - No-ops gracefully in non-browser/SSR environments.
 *
 * @param text Text to copy
 * @param title Optional success title
 * @param message Optional success message
 * @returns Promise<boolean> resolves true on success, false on failure
 */
export async function copyToClipboard(
  text: string,
  title: string = 'Kopiert',
  message?: string
): Promise<boolean> {
  const notify = safeNotify()

  try {
    // SSR / non-browser guard
    if (typeof window === 'undefined' || typeof document === 'undefined') {
      notify('error', 'Fehler', 'Kopieren ist in dieser Umgebung nicht verfügbar.')
      return false
    }

    // Prefer async Clipboard API when available and in secure context
    if (navigator?.clipboard && window.isSecureContext) {
      try {
        // Try permission query if available to hint user agent behavior
        if ((navigator as any).permissions?.query) {
          try {
            const status = await (navigator as any).permissions.query({ name: 'clipboard-write' as any })
            // status.state can be 'granted' | 'denied' | 'prompt'
            if (status.state === 'denied') throw new Error('Clipboard permission denied')
          } catch {
            // Ignore permission query errors and attempt write regardless
          }
        }
        await navigator.clipboard.writeText(text)
        notify('success', title, message ?? 'In die Zwischenablage kopiert.')
        return true
      } catch {
        // Fallback below
      }
    }

    // Fallback: execCommand using a temporary textarea
    const textarea = document.createElement('textarea')
    textarea.value = text
    // Avoid scrolling to bottom
    textarea.style.position = 'fixed'
    textarea.style.top = '0'
    textarea.style.left = '0'
    textarea.style.opacity = '0'
    textarea.setAttribute('readonly', '')

    document.body?.appendChild(textarea)
    textarea.select()
    textarea.setSelectionRange(0, textarea.value.length)

    const ok = document.execCommand ? document.execCommand('copy') : false

    textarea.remove()

    if (ok) {
      notify('success', title, message ?? 'In die Zwischenablage kopiert.')
      return true
    }

    notify('error', 'Fehler', 'Kopieren fehlgeschlagen.')
    return false
  } catch {
    notify('error', 'Fehler', 'Kopieren fehlgeschlagen.')
    return false
  }
}

function safeNotify() {
  try {
    const store = useNotifyStore()
    return (alertType: 'success' | 'error' | 'warning' | 'info', title: string, message: string) =>
      store.createNotify({ id: uuid(), alertType, title, message })
  } catch {
    // Pinia store might not be available in isolated tests. Gracefully no-op.
    return () => {}
  }
}
